import { FC, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSnackbar } from 'notistack';
import { useAuth } from 'src/contexts/JWTContext';
import { useSettings } from 'src/contexts/SettingsContext';
import useGeneralError from 'src/hooks/useGeneralError';
import { useGoogleLogin } from '@react-oauth/google';
import LoginButton from 'src/pages/authentication/components/LoginButton';
import { ButtonProps } from 'material-hu';
import { getGoogleInstances } from 'src/services/authService';
import googleLogo from 'src/assets/google.svg';
import 'src/components/authentication/i18n';

export type GoogleLoginProps = ButtonProps & {
  onLogin: () => void;
  onLoginFinally: () => void;
};

export const GoogleLogin: FC<GoogleLoginProps> = (props) => {
  const {
    onLogin = () => null,
    onLoginFinally = () => null,
    ...buttonProps
  } = props;

  const { t } = useTranslation(['AUTH']);
  const { enqueueSnackbar } = useSnackbar();
  const showGeneralError = useGeneralError();
  const { googleLogin } = useAuth();
  const { setPrimary, resetPrimary } = useSettings();

  const googleLoginHook = useGoogleLogin({
    onSuccess: async ({ access_token }) => {
      try {
        const response = await getGoogleInstances(access_token);
        if (response.data?.length) {
          const instance = response.data[0];
          setPrimary(instance.color);
          const body = {
            accessToken: access_token,
            instanceId: instance.id,
          };
          await googleLogin(body);
        }
        else {
          enqueueSnackbar(
            t('ERROR_GOOGLE_GET_INSTANCES'),
            { variant: 'error' },
          );
        }
      }
      catch (err) {
        showGeneralError(err, t('ERROR_GOOGLE_LOGIN'));
      }
      finally {
        onLoginFinally();
      }
    },
    onError: (err) => {
      showGeneralError(err, t('ERROR_GOOGLE_LOGIN'));
    },
    onNonOAuthError: () => {
      onLoginFinally();
    },
  });

  useEffect(() => {
    resetPrimary();
  }, [resetPrimary]);


  const handleLogin = () => {
    onLogin();
    googleLoginHook();
  };

  return (
    <>
      <LoginButton
        logo={googleLogo}
        label="Google"
        onClick={handleLogin}
        {...buttonProps}
      />
    </>
  );
};

export default GoogleLogin;
